import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { COPYRIGHT_NOTICE } from "@/constants/legal";
import { useAuth } from "@/context/AuthContext";
import AssistantWidget from "@/components/AssistantWidget";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  Cable, LayoutDashboard, Warehouse, Truck, Users, Building2, ListChecks,
  ClipboardList, FileText, ScanLine, LogOut, Menu, X, Package, Settings, Trophy, FileCheck, Car, CalendarClock, CalendarDays, Mail, Shield, CreditCard, Wrench, Receipt, Activity, BookOpen, FolderOpen, Target, Coins, Sparkles, FolderUp, MapPinned, MailCheck, BarChart3
} from "lucide-react";

const buildNav = (t) => [
  { to: "/dashboard", label: t("menu.dashboard"), icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/super-admin", label: "🌍 Super Admin", icon: Shield, testid: "nav-super-admin", superAdminOnly: true },
  { to: "/magazzino", label: t("menu.warehouse_general"), icon: Warehouse, testid: "nav-magazzino", roles: ["admin", "magazziniere", "assistente"] },
  { to: "/magazzini", label: t("menu.warehouses"), icon: Building2, testid: "nav-magazzini", roles: ["admin", "magazziniere", "assistente"] },
  { to: "/viaggiante", label: t("menu.warehouse_traveling"), icon: Truck, testid: "nav-viaggiante" },
  { to: "/scarico-materiali", label: "Scarico Materiali", icon: Sparkles, testid: "nav-scarico-materiali", roles: ["admin", "magazziniere"] },
  { to: "/tecnici-in-campo", label: "Visualizza tecnici in campo", icon: MapPinned, testid: "nav-tecnici-in-campo", roles: ["admin", "magazziniere"] },
  { to: "/lettura-mail", label: "Lettura Mail Seriali", icon: MailCheck, testid: "nav-lettura-mail", roles: ["admin", "magazziniere"] },
  { to: "/produzione-committente", label: "Produzione per Committente", icon: BarChart3, testid: "nav-produzione-committente", roles: ["admin"] },
  { to: "/documenti-massivi", label: "Inserimento Documenti Massivi", icon: FolderUp, testid: "nav-documenti-massivi", roles: ["admin", "magazziniere"] },
  { to: "/automezzi", label: t("menu.vehicles"), icon: Car, testid: "nav-automezzi", roles: ["admin", "magazziniere", "assistente"] },
  { to: "/attrezzatura", label: t("menu.equipment"), icon: Wrench, testid: "nav-attrezzatura", roles: ["admin", "magazziniere", "assistente"] },
  { to: "/assegnazioni", label: t("menu.assignments"), icon: CalendarClock, testid: "nav-assegnazioni", roles: ["admin", "magazziniere", "assistente"] },
  { to: "/miei-appuntamenti", label: "I Miei Appuntamenti", icon: CalendarClock, testid: "nav-miei-appuntamenti", roles: ["tecnico"] },
  { to: "/account-email", label: t("menu.email_account"), icon: Mail, testid: "nav-account-email", roles: ["admin", "magazziniere", "assistente"] },
  { to: "/documenti-condivisi", label: t("menu.shared_docs"), icon: FolderOpen, testid: "nav-shared-docs", roles: ["admin", "assistente"] },
  { to: "/materiali", label: t("menu.materials"), icon: Package, testid: "nav-materiali", roles: ["admin", "magazziniere", "assistente"] },
  { to: "/tecnici", label: t("menu.users"), icon: Users, testid: "nav-tecnici", roles: ["admin", "magazziniere", "assistente"] },
  { to: "/committenti", label: t("menu.clients"), icon: Building2, testid: "nav-committenti", roles: ["admin", "magazziniere", "assistente"] },
  { to: "/listini", label: t("menu.price_lists"), icon: ListChecks, testid: "nav-listini", roles: ["admin"] },
  { to: "/lavorazioni", label: t("menu.works"), icon: ClipboardList, testid: "nav-lavorazioni", roles: ["admin", "magazziniere", "tecnico"] },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, testid: "nav-agenda" },
  { to: "/chiusure", label: t("menu.closure_reports"), icon: FileCheck, testid: "nav-chiusure", roles: ["admin", "magazziniere", "assistente"] },
  { to: "/bef", label: t("menu.bef"), icon: FileText, testid: "nav-bef", roles: ["admin", "magazziniere"] },
  { to: "/cedolini", label: "Cedolini", icon: Coins, testid: "nav-cedolini", roles: ["admin"] },
  { to: "/rimborso-km", label: "Scheda Rimborso Chilometrico", icon: Car, testid: "nav-rimborso-km", roles: ["admin"] },
  { to: "/preventivi", label: t("menu.quotes"), icon: Receipt, testid: "nav-preventivi", roles: ["admin"] },
  { to: "/performance", label: t("menu.performance"), icon: Activity, testid: "nav-performance", roles: ["admin", "assistente"] },
  { to: "/produzione-obiettivo", label: "Produzione Obiettivo", icon: Target, testid: "nav-produzione-obiettivo", roles: ["admin"] },
  { to: "/ranking", label: t("menu.ranking"), icon: Trophy, testid: "nav-ranking", roles: ["admin"] },
  { to: "/install", label: "Chiusura impianto", icon: ScanLine, testid: "nav-install" },
  { to: "/abbonamento", label: t("menu.subscription"), icon: CreditCard, testid: "nav-abbonamento", roles: ["admin"] },
  { to: "/manuale", label: t("menu.manual"), icon: BookOpen, testid: "nav-manuale", roles: ["admin"] },
];

export default function AppLayout({ children }) {
  const { user, logout, settings } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const nav = buildNav(t);

  const doLogout = async () => { await logout(); navigate("/login"); };
  const brand = settings?.brand_name || "EVOSOFT";
  const logo = settings?.logo_data_url;

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      {/* Mobile top bar */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
        <button onClick={() => setOpen(!open)} className="p-2" data-testid="mobile-menu-toggle">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
        <div className="flex items-center gap-2">
          {logo ? (
            <img src={logo} alt="logo" className="h-8 w-8 rounded-sm object-cover" />
          ) : (
            <div className="grid h-8 w-8 place-items-center rounded-sm" style={{ backgroundColor: "var(--brand-color)" }}>
              <Cable className="h-4 w-4 text-white" />
            </div>
          )}
          <span className="font-mono text-xs tracking-widest">{brand}</span>
        </div>
        <div className="w-8" />
      </div>

      <div className="flex">
        <aside
          className={`sidebar-shell fixed inset-y-0 left-0 z-40 w-64 transform text-white transition-transform duration-200 lg:static lg:translate-x-0 ${
            open ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex h-full flex-col">
            <div className="flex items-center gap-3 border-b px-5 py-5" style={{ borderColor: "var(--sidebar-divider)" }}>
              {logo ? (
                <img src={logo} alt="logo" className="h-9 w-9 rounded-sm object-cover" />
              ) : (
                <div className="grid h-9 w-9 place-items-center rounded-sm" style={{ backgroundColor: "var(--brand-color)" }}>
                  <Cable className="h-4 w-4" />
                </div>
              )}
              <div className="min-w-0">
                <div className="truncate font-mono text-sm tracking-widest">{brand}</div>
                <div className="truncate text-[10px] uppercase tracking-widest" style={{ color: "var(--sidebar-text)", opacity: 0.7 }}>{settings?.name || "Gestionale impianti"}</div>
              </div>
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto p-3">
              {nav.filter((n) => {
                if (n.superAdminOnly) return !!user?.is_super_admin;
                if (n.roles && !n.roles.includes(user?.role)) return false;
                // Toggle admin: nasconde "Lavorazioni" al tecnico se disattivato
                if (n.to === "/lavorazioni" && user?.role === "tecnico" && user?.show_lavorazioni === false) return false;
                // Toggle admin: nasconde "Chiusura Impianto" al tecnico se disabilitata
                if (n.to === "/install" && user?.role === "tecnico" && user?.disable_chiusura === true) return false;
                // RBAC granulare admin: pagine nascoste esplicitamente per questo utente
                if (Array.isArray(user?.hidden_pages) && user.role !== "admin" && user.hidden_pages.includes(n.to)) return false;
                return true;
              }).map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={n.testid}
                    className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{n.label}</span>
                  </NavLink>
                );
              })}
              {user?.role && (
                <NavLink to="/impostazioni" onClick={() => setOpen(false)} data-testid="nav-settings" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}>
                  <Settings className="h-4 w-4" />
                  <span>{t("common.settings")}</span>
                </NavLink>
              )}
            </nav>
            <div className="border-t p-3" style={{ borderColor: "var(--sidebar-divider)" }}>
              <div className="mb-2 flex items-center justify-between px-1">
                <div className="text-xs">
                  <div className="font-medium text-white">{user?.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--sidebar-text)", opacity: 0.7 }}>{user?.role}</div>
                </div>
                <LanguageSwitcher compact />
              </div>
              <button
                onClick={doLogout}
                data-testid="logout-btn"
                className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm text-white transition-colors duration-150"
                style={{ backgroundColor: "var(--sidebar-hover-bg)" }}
              >
                <LogOut className="h-4 w-4" /> {t("common.logout")}
              </button>
            </div>
          </div>
        </aside>

        {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

        <main className="relative flex min-h-screen min-w-0 flex-1 flex-col p-4 lg:p-8">
          {/* Logo watermark di sfondo (fixed, non scrolla) - controllato da impostazioni */}
          {logo && (settings?.show_logo_watermark !== false) && (
            <div
              aria-hidden="true"
              className="pointer-events-none fixed inset-0 z-0 hidden lg:block"
              style={{
                backgroundImage: `url(${logo})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center center",
                backgroundSize: "min(60vw, 780px) auto",
                opacity: 0.12,
                filter: "grayscale(30%)",
              }}
            />
          )}
          <div className="relative z-10 flex-1">
            {children}
          </div>
          <footer className="relative z-10 mt-10 border-t border-slate-200 pt-4 text-center text-xs text-slate-400" data-testid="app-copyright">
            {COPYRIGHT_NOTICE}
          </footer>
        </main>
      </div>
      <AssistantWidget />
    </div>
  );
}
